import DecliningBalanceFlyoutSvg from 'ext-ui/svg/illustration/DecliningBalanceFlyoutSvg'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CustomVirtualCardAddressFlatFields } from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import { PhysicalVirtualCardAddressFlatFields } from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import RecipientsTooltip from 'src/components/RecipientsTooltip/RecipientsTooltip'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  BillPayRecipient,
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, OrganizationRoles, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserAssociation } from 'src/models/user.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { buildLowLimitAlert } from 'src/utils/lowLimitAlert.utils'
import { css } from 'styled-components'

interface CreateVirtualCardStandardContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  postBulkVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostBulkVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout?: (
    body: Partial<PostVirtualCardRequest>,
  ) => void
  setScreen: (val: CreateVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  pageLocation: string
  showSoftAlert?: boolean
  isBalanceLocked?: boolean
  isBillPay?: boolean
}

const CreateVirtualCardStandardContent: React.FC<
  CreateVirtualCardStandardContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const user = useSelector(selectUserListModelFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const translate = useTranslate()
  const standardCardImage = useColorOrGrayscaleImage(
    ImageNames.CardTypeStandard,
  )
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const lowBalanceAlertFeatureEnabled = !!useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_low_balance_alert

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrence: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
    role?: OrganizationRoles,
  ): void => {
    const fields = {
      creditCardId: flatFields.creditCardId,
      displayName: props.isBillPay
        ? flatFields.vendorName
        : flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      address1: flatFields.customBillingAddress
        ? customAddressFields.address1
        : undefined,
      address2: flatFields.customBillingAddress
        ? customAddressFields.address2
        : undefined,
      city: flatFields.customBillingAddress
        ? customAddressFields.city
        : undefined,
      province: flatFields.customBillingAddress
        ? customAddressFields.province
        : undefined,
      postal: flatFields.customBillingAddress
        ? customAddressFields.postal
        : undefined,
      country: flatFields.customBillingAddress
        ? customAddressFields.country
        : undefined,
      hasPlasticCard: flatFields.includePhysicalCard,
      plasticFirstName: flatFields.includePhysicalCard
        ? flatFields.plasticFirstName
        : undefined,
      plasticLastName: flatFields.includePhysicalCard
        ? flatFields.plasticLastName
        : undefined,
      plasticAddress1:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address1
          : undefined,
      plasticAddress2:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address2
          : undefined,
      plasticCity:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.city
          : undefined,
      plasticProvince:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.province
          : undefined,
      plasticPostal:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.postal
          : undefined,
      plasticCountry:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.country
          : undefined,
      singleExactPay: flatFields.singleExactPay,
      balanceLocked: props.isBillPay
        ? undefined
        : props.isBalanceLocked || undefined,
      lowLimitAlert: buildLowLimitAlert(
        flatFields,
        lowBalanceAlertFeatureEnabled,
      ),
    }

    if (props.isBillPay) {
      props.dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout?.({
        ...fields,
        receiptRulesExempt: true,
        recipient: user?.email,
        billPayMeta: {
          invoiceNumber: flatFields.invoiceNumber,
          invoiceDate: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
          vendorName: flatFields.vendorName,
          vendorEmail:
            flatFields.billPayRecipient === BillPayRecipient.Vendor
              ? flatFields.vendorEmail
              : undefined,
        },
      })

      analytics?.track('Sucessfully create virtual card', {
        type_of_card: VirtualCardTypes.billPay,
        vendor_name: flatFields.vendorName,
        vendor_email: flatFields.vendorEmail,
        invoice_number: flatFields.invoiceNumber,
        invoice_date: flatFields.invoiceDateMoment?.format('YYYY-MM-DD'),
        credit_limit: flatFields.balanceDollars,
        active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
        expense_category: Object.keys(expenseDetails).length > 0,
        add_attachments:
          props.receiptAttachments && props.receiptAttachments.length > 0,
        add_notes: !!flatFields.notes,
        is_recipient_me:
          flatFields.billPayRecipient === BillPayRecipient.Myself,
        receipt_rules_enabled: !!flatFields.receiptRulesExempt,
        issuer:
          creditCards &&
          flatFields.creditCardId &&
          creditCards[flatFields.creditCardId]
            ? creditCards[flatFields.creditCardId].issuer
            : undefined,
      })
    } else {
      flatFields?.userEmails?.length === 1
        ? props.postVirtualCardAndOpenToastAndCloseFlyout(
            {
              ...fields,
              recipient: flatFields.userEmails[0],
              receiptRulesExempt: flatFields.receiptRulesExempt,
            },
            true,
            role,
          )
        : props.postBulkVirtualCardAndOpenToastAndCloseFlyout(
            {
              ...fields,
              recipients: flatFields.userEmails,
            },
            true,
            role,
          )

      analytics?.track('Sucessfully create virtual card', {
        type_of_card: props.isBalanceLocked
          ? VirtualCardTypes.decliningBalance
          : VirtualCardTypes.standard,
        card_name: flatFields.displayName,
        credit_limit: flatFields.balanceDollars,
        active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
        expense_category: Object.keys(expenseDetails).length > 0,
        add_attachments:
          props.receiptAttachments && props.receiptAttachments.length > 0,
        add_notes: !!flatFields.notes,
        recipient_count: flatFields.userEmails?.length,
        is_recipient_me:
          flatFields.userEmails &&
          user &&
          flatFields.userEmails.includes(user.email),
        receipt_rules_enabled: !!flatFields.receiptRulesExempt,
        issuer:
          creditCards &&
          flatFields.creditCardId &&
          creditCards[flatFields.creditCardId]
            ? creditCards[flatFields.creditCardId].issuer
            : undefined,
      })
    }
  }

  const initialFlatFields = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  if (props.isBillPay) {
    return (
      <ActionRightFlyoutContent
        title={translate('Pay an Invoice')}
        specificElements={{
          imageSrc: guestCardImage,
        }}
        pickerSubtitle={translate(
          'Create a virtual card to make an invoice payment',
        )}
      >
        <VirtualCardForm
          id={'create'}
          buttonText={translate('Create a Virtual Card')}
          billPayShareButtonText={translate(
            'Create Virtual Card and Share with Vendor',
          )}
          onSuccessfulSubmit={onSuccessfulSubmit}
          loading={props.postingVirtualCard}
          userFieldLabel={translate('Recipients')}
          recipientNamesEditable={false}
          recurrenceEditable={false}
          notesEditable
          creditCardEditable
          amountProps={{
            amountEditable: true,
          }}
          initialFlatFields={initialFlatFields}
          creditCardProps={{
            isFormDisabled: props.isFormDisabled,
            creditCardOptions: props.creditCardOptions,
            labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
          }}
          attachmentsProps={{
            sectionHeader: translate('Invoice'),
            sectionSubheader: translate(
              'Use this space to attach a vendor invoice. You will be able to attach the settled invoice once the payment clears.',
            ),
            receiptAttachments: props.receiptAttachments,
            uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
            uploadAttachments: props.uploadAttachments,
            deleteAttachment: props.deleteAttachment,
          }}
          topLevelError={props.topLevelError}
          errorDetailMap={props.errorDetailMap}
          creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          typeofCard={VirtualCardTypes.billPay}
          showCardApprovalSoftAlert={props.showSoftAlert}
        />
      </ActionRightFlyoutContent>
    )
  }

  return (
    <ActionRightFlyoutContent
      title={translate('Create a Virtual Card')}
      specificElements={{
        subtitle: props.isBalanceLocked
          ? translate('Declining Balance Card')
          : translate('Standard'),
        imageSrc: props.isBalanceLocked ? undefined : standardCardImage,
        imageComponent: props.isBalanceLocked ? (
          <DecliningBalanceFlyoutSvg />
        ) : undefined,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: translate('Change Card Type'),
          onChangeClick: (): void => {
            props.setScreen(CreateVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        id={'create'}
        buttonText={translate('Create a Virtual Card')}
        multiCardButtonText={translate('Create Virtual Cards')}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={translate('Recipients')}
        customAddressEditable
        physicalCardEditable
        recipientNamesEditable={false}
        recurrenceEditable={false}
        notesEditable
        creditCardEditable
        amountProps={{
          amountEditable: true,
        }}
        initialFlatFields={initialFlatFields}
        creditCardProps={{
          isFormDisabled: props.isFormDisabled,
          creditCardOptions: props.creditCardOptions,
          labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
        }}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        tooltip={
          <RecipientsTooltip
            openLeft={false}
            css={css`
              z-index: 1;
              padding-bottom: 6px;
            `}
          />
        }
        isMulti
        suggestedProps={{
          showSuggested: true,
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardTo]}
        unableToAddNewUser={
          showGuestRole ? false : !user?.hasOrgAdminPermissions
        }
        typeofCard={VirtualCardTypes.standard}
        showCardApprovalSoftAlert={props.showSoftAlert}
      />
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardStandardContent
